import { Pencil, Church, Lock, Flag, UserPlus, UserCheck, MessageCircle } from "lucide-react";
import { Avatar, InstitutionMark, VerifiedBadge } from "@/components/Brand";
import { formatCount } from "@/lib/api";
import { AvatarUploader } from "@/components/profile/AvatarUploader";

const Stat = ({ value, label, testid, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} className="text-left transition-opacity enabled:hover:opacity-80" data-testid={testid}>
    <p className="text-xl font-extrabold">{formatCount(value)}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </button>
);

function FollowButton({ p, onFollow, busy }) {
  if (!p.can_follow) return null;
  const on = p.is_following;
  return (
    <button onClick={onFollow} disabled={busy} data-testid="profile-follow-button" data-following={on ? "true" : "false"}
      className={`inline-flex h-10 w-fit items-center gap-2 rounded-full px-6 text-sm font-extrabold transition-[background-color,transform] active:scale-95 disabled:opacity-60 ${on ? "bg-white/10 text-white hover:bg-white/15" : "bg-[hsl(var(--gold))] text-[#0B0F17] hover:brightness-110"}`}>
      {on ? <><UserCheck size={16} /> Seguindo</> : <><UserPlus size={16} /> Seguir</>}
    </button>
  );
}

export function ProfileHeader({ p, onEdit, onAvatar, onFollow, onReport, followBusy, onList, onMessage }) {
  const inst = p.role === "institution";
  return (
    <section className="flex flex-col gap-5 py-8 sm:flex-row sm:items-center sm:gap-8" data-testid="profile-header">
      <div className="relative w-fit rounded-full ring-2 ring-[hsl(var(--gold))]/60 ring-offset-4 ring-offset-background" data-testid="profile-avatar">
        {inst && !p.has_custom_avatar ? <InstitutionMark name={p.name} logo={p.avatar} color={p.brand_color} size={96} /> : <Avatar name={p.name} src={p.avatar} size={96} className="text-2xl" />}
        {p.is_me && <AvatarUploader onChange={onAvatar} />}
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="profile-name">{p.name}</h1>
          {p.verified && <VerifiedBadge size={22} />}
          {inst && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Instituição</span>}
        </div>
        {p.community && <p className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--gold))]" data-testid="profile-community"><Church size={14} /> {p.community}</p>}
        {p.bio && <p className="max-w-xl text-sm text-white/85" data-testid="profile-bio">{p.bio}</p>}
        {p.private && <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="profile-private-note"><Lock size={12} /> Perfil protegido de menor de idade</p>}
        {p.is_me && !p.bio && !p.community && <p className="text-sm text-muted-foreground">Adicione uma biografia e a sua comunidade.</p>}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-2">
          <Stat value={p.stats.posts} label="Publicações" testid="profile-stat-posts" />
          <Stat value={p.stats.followers} label="Seguidores" testid="profile-stat-followers" onClick={() => onList("followers")} />
          <Stat value={p.stats.following} label="Seguindo" testid="profile-stat-following" onClick={() => onList("following")} />
          <Stat value={p.stats.likes} label="Curtidas" testid="profile-stat-likes" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        {p.is_me ? (
          <button onClick={onEdit} className="inline-flex h-10 w-fit items-center gap-2 rounded-full bg-white/10 px-5 text-sm font-bold transition-colors hover:bg-white/15" data-testid="profile-edit-button">
            <Pencil size={15} /> Editar perfil
          </button>
        ) : <><FollowButton p={p} onFollow={onFollow} busy={followBusy} />
          {p.can_message && <button onClick={onMessage} className="inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-5 text-sm font-bold transition-colors hover:bg-white/15" data-testid="profile-message-button"><MessageCircle size={15} /> Mensagem</button>}</>}
        {!p.is_me && p.has_custom_avatar && (
          <button onClick={onReport} aria-label="Denunciar foto de perfil" title="Denunciar foto de perfil" data-testid="profile-report-avatar-button"
            className="grid h-10 w-10 place-items-center rounded-full bg-white/5 text-muted-foreground transition-colors hover:bg-red-500/20 hover:text-red-300">
            <Flag size={15} />
          </button>
        )}
      </div>
    </section>
  );
}
